import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  getFiscalConfig,
  saveFiscalConfig,
  generateAfipCSR,
  deleteAfipCertificate,
  uploadAfipCertificate,
  getAfipStatus,
} from '../services/api';
import { useLock } from '../context/LockContext';
import PinVerifyModal from '../components/PinVerifyModal';
import HelpIcon from '../components/help/HelpIcon';
import { storage } from '../utils/storage';
import './FiscalConfigPage.css';

const CONDICIONES_IVA_VALUES = [1, 4, 6];

/**
 * Validates an Argentine CUIT/CUIL using the Mod 11 check-digit algorithm.
 * @param {string} cuit — digits only, length 11
 * @returns {boolean}
 */
const isValidCuit = (cuit) => {
  const digits = cuit.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, w, i) => acc + w * parseInt(digits[i], 10), 0);
  const remainder = sum % 11;
  const expectedCheck = remainder === 0 ? 0 : remainder === 1 ? 9 : 11 - remainder;
  return parseInt(digits[10], 10) === expectedCheck;
};

const FiscalConfigPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPin } = useLock();
  const [pinVerified, setPinVerified] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);

  useEffect(() => {
    if (hasPin && !pinVerified) {
      setShowPinModal(true);
    }
  }, [hasPin, pinVerified]);

  const [config, setConfig] = useState({
    cuit: '',
    razonSocial: '',
    condicionIVA: '',
    domicilioComercial: '',
    puntoDeVenta: '',
    iibbNumber: '',
    actividadInicio: '',
    afipEnvironment: 'homologacion',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [afipStatus, setAfipStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [hasCertificate, setHasCertificate] = useState(false);
  const [generatingCSR, setGeneratingCSR] = useState(false);
  const [csrData, setCsrData] = useState(null);
  const [certMode, setCertMode] = useState('guided'); // 'guided' | 'manual'
  const [message, setMessage] = useState(null);

  const restaurantId = storage.get('selectedRestaurantId');

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getFiscalConfig(restaurantId);
      if (result.data) {
        setConfig({
          cuit: result.data.cuit || '',
          razonSocial: result.data.razonSocial || '',
          condicionIVA: result.data.condicionIVA || '',
          domicilioComercial: result.data.domicilioComercial || '',
          puntoDeVenta: result.data.puntoDeVenta || '',
          iibbNumber: result.data.iibbNumber || '',
          actividadInicio: result.data.actividadInicio || '',
          afipEnvironment: result.data.afipEnvironment || 'homologacion',
        });
        setHasCertificate(result.data.hasCertificate || false);
        if (result.data.csrGeneratedAt) {
          setCsrData({ generatedAt: result.data.csrGeneratedAt });
        }
      }
    } catch (err) {
      console.error('Error loading fiscal config:', err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const validateForm = () => {
    const errors = {};
    const cuitDigits = config.cuit.replace(/\D/g, '');

    if (!cuitDigits) {
      errors.cuit = t('fiscal.cuitRequired', 'El CUIT es obligatorio');
    } else if (cuitDigits.length !== 11) {
      errors.cuit = t('fiscal.cuitLength', 'El CUIT debe tener 11 dígitos');
    } else if (!isValidCuit(cuitDigits)) {
      errors.cuit = t('fiscal.cuitInvalid', 'El CUIT es inválido (dígito verificador incorrecto)');
    }

    if (!config.razonSocial.trim()) {
      errors.razonSocial = t('fiscal.razonSocialRequired', 'La Razón Social es obligatoria');
    }

    if (!config.condicionIVA) {
      errors.condicionIVA = t(
        'fiscal.condicionIVARequired',
        'Seleccione una condición frente al IVA'
      );
    }

    if (!config.puntoDeVenta) {
      errors.puntoDeVenta = t('fiscal.puntoDeVentaRequired', 'El Punto de Venta es obligatorio');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setSaving(true);
      setMessage(null);
      await saveFiscalConfig(restaurantId, {
        ...config,
        condicionIVA: parseInt(config.condicionIVA, 10),
        puntoDeVenta: parseInt(config.puntoDeVenta, 10),
      });
      setMessage({
        type: 'success',
        text: t('fiscal.configSaved', 'Configuración fiscal guardada exitosamente'),
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.details?.join(', ') || t('fiscal.configError', 'Error al guardar'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCSR = async () => {
    if (!config.cuit || !config.razonSocial) {
      setMessage({
        type: 'error',
        text: t('fiscal.csrRequiresData', 'Complete CUIT y Razón Social antes de generar el CSR'),
      });
      return;
    }
    try {
      setGeneratingCSR(true);
      setMessage(null);
      const result = await generateAfipCSR(restaurantId);
      setCsrData(result.data);
      setMessage({
        type: 'success',
        text: t('fiscal.csrGenerated', 'CSR generado exitosamente. Descárguelo y súbalo a AFIP.'),
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || t('fiscal.csrError', 'Error al generar CSR'),
      });
    } finally {
      setGeneratingCSR(false);
    }
  };

  const handleDownloadCSR = () => {
    if (!csrData?.csr) return;
    const blob = new Blob([csrData.csr], { type: 'application/pkcs10' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const alias = config.razonSocial?.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30) || 'afip';
    a.href = url;
    a.download = `${alias}.csr`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUploadCertificate = async () => {
    // In guided mode (CSR flow), only need the .crt
    // In manual mode, need both .crt and .key
    if (certMode === 'manual' && (!certFile || !keyFile)) {
      setMessage({
        type: 'error',
        text: t('fiscal.certRequired', 'Seleccione certificado (.crt) y clave privada (.key)'),
      });
      return;
    }
    if (certMode === 'guided' && !certFile) {
      setMessage({
        type: 'error',
        text: t('fiscal.certFileRequired', 'Seleccione el certificado (.crt) firmado por AFIP'),
      });
      return;
    }
    try {
      setUploadingCert(true);
      setMessage(null);
      const certContent = await certFile.text();
      const payload = { certificate: certContent };
      if (certMode === 'manual' && keyFile) {
        payload.privateKey = await keyFile.text();
      }
      await uploadAfipCertificate(restaurantId, payload);
      setHasCertificate(true);
      setCertFile(null);
      setKeyFile(null);
      setMessage({
        type: 'success',
        text: t('fiscal.certUploaded', 'Certificado subido exitosamente'),
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || t('fiscal.certError', 'Error al subir certificado'),
      });
    } finally {
      setUploadingCert(false);
    }
  };

  const handleDeleteCertificate = async () => {
    if (
      !window.confirm(
        t(
          'fiscal.confirmDeleteCert',
          '¿Eliminar certificado y clave privada? Deberá volver a configurarlos.'
        )
      )
    ) {
      return;
    }
    try {
      setMessage(null);
      await deleteAfipCertificate(restaurantId);
      setHasCertificate(false);
      setCsrData(null);
      setCertFile(null);
      setKeyFile(null);
      setAfipStatus(null);
      setMessage({
        type: 'success',
        text: t('fiscal.certDeleted', 'Certificado eliminado exitosamente'),
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error || t('fiscal.certDeleteError', 'Error al eliminar certificado'),
      });
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setMessage(null);
      const result = await getAfipStatus(restaurantId);
      setAfipStatus(result.data);
      if (result.data.status === 'connected') {
        setMessage({ type: 'success', text: t('fiscal.connected', 'Conexión con AFIP exitosa') });
      } else if (result.data.status === 'error') {
        setMessage({
          type: 'error',
          text: result.data.message || t('fiscal.connectionError', 'Error de conexión'),
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: t('fiscal.connectionError', 'Error al verificar conexión con AFIP'),
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const formatCuit = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 11);
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 10) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10)}`;
  };

  if (hasPin && !pinVerified) {
    return (
      <div className="fiscal-config-page">
        <div className="fiscal-pin-gate">
          <span className="material-icons fiscal-pin-gate-icon">lock</span>
          <h2>{t('fiscal.pinRequired', 'Verificación requerida')}</h2>
          <p>
            {t(
              'fiscal.pinGateMessage',
              'Los datos fiscales están protegidos. Ingrese su PIN para acceder.'
            )}
          </p>
          <button type="button" className="btn btn-primary" onClick={() => setShowPinModal(true)}>
            {t('lock.unlock', 'Desbloquear')}
          </button>
        </div>
        {showPinModal && (
          <PinVerifyModal
            onVerified={() => {
              setPinVerified(true);
              setShowPinModal(false);
            }}
            onClose={() => {
              setShowPinModal(false);
              navigate('/settings');
            }}
          />
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fiscal-config-page">
        <div className="page-loading">{t('common.loading', 'Cargando...')}</div>
      </div>
    );
  }

  return (
    <div className="fiscal-config-page">
      <div className="fiscal-config-header">
        <h1>
          {t('fiscal.title', 'Configuración Fiscal')} <HelpIcon topicKey="fiscal.overview" />
        </h1>
        <p className="fiscal-config-subtitle">
          {t(
            'fiscal.subtitle',
            'Configure los datos fiscales para emitir facturas electrónicas AFIP'
          )}
        </p>
      </div>

      {message && (
        <div className={`fiscal-message fiscal-message--${message.type}`}>
          <span className="material-icons">
            {message.type === 'success' ? 'check_circle' : 'error'}
          </span>
          {message.text}
        </div>
      )}

      {/* Status indicator */}
      <div className="fiscal-status-bar">
        <div className={`fiscal-status-indicator ${hasCertificate ? 'configured' : 'pending'}`}>
          <span className="material-icons">{hasCertificate ? 'verified' : 'pending'}</span>
          <span>
            {hasCertificate
              ? t('fiscal.certificateOk', 'Certificado configurado')
              : t('fiscal.noCertificate', 'Certificado pendiente')}
          </span>
        </div>
        {afipStatus && (
          <div
            className={`fiscal-status-indicator ${afipStatus.status === 'connected' ? 'configured' : 'error'}`}
          >
            <span className="material-icons">
              {afipStatus.status === 'connected' ? 'cloud_done' : 'cloud_off'}
            </span>
            <span>AFIP: {afipStatus.status === 'connected' ? 'Conectado' : afipStatus.status}</span>
          </div>
        )}
      </div>

      <div className="fiscal-config-grid">
        {/* Datos fiscales */}
        <div className="fiscal-card">
          <h2 className="fiscal-card-title">
            <span className="material-icons">business</span>
            {t('fiscal.companyData', 'Datos de la Empresa')}
          </h2>
          <form onSubmit={handleSave}>
            <div className={`fiscal-form-group ${fieldErrors.cuit ? 'has-error' : ''}`}>
              <label htmlFor="fiscal-cuit">
                {t('fiscal.cuit', 'CUIT')} <span className="fiscal-required">*</span>
              </label>
              <input
                id="fiscal-cuit"
                type="text"
                value={formatCuit(config.cuit)}
                onChange={(e) => {
                  setConfig({ ...config, cuit: e.target.value.replace(/\D/g, '') });
                  if (fieldErrors.cuit) setFieldErrors((prev) => ({ ...prev, cuit: undefined }));
                }}
                placeholder="XX-XXXXXXXX-X"
                maxLength={13}
                required
                aria-required="true"
                aria-invalid={!!fieldErrors.cuit}
                aria-describedby={fieldErrors.cuit ? 'cuit-error' : undefined}
              />
              {fieldErrors.cuit && (
                <span id="cuit-error" className="fiscal-field-error" role="alert">
                  {fieldErrors.cuit}
                </span>
              )}
            </div>

            <div className={`fiscal-form-group ${fieldErrors.razonSocial ? 'has-error' : ''}`}>
              <label htmlFor="fiscal-razon-social">
                {t('fiscal.razonSocial', 'Razón Social')} <span className="fiscal-required">*</span>
              </label>
              <input
                id="fiscal-razon-social"
                type="text"
                value={config.razonSocial}
                onChange={(e) => {
                  setConfig({ ...config, razonSocial: e.target.value });
                  if (fieldErrors.razonSocial)
                    setFieldErrors((prev) => ({ ...prev, razonSocial: undefined }));
                }}
                placeholder="Nombre legal de la empresa"
                required
                aria-required="true"
                aria-invalid={!!fieldErrors.razonSocial}
                aria-describedby={fieldErrors.razonSocial ? 'razon-social-error' : undefined}
              />
              {fieldErrors.razonSocial && (
                <span id="razon-social-error" className="fiscal-field-error" role="alert">
                  {fieldErrors.razonSocial}
                </span>
              )}
            </div>

            <div className={`fiscal-form-group ${fieldErrors.condicionIVA ? 'has-error' : ''}`}>
              <label htmlFor="fiscal-condicion-iva">
                {t('fiscal.condicionIVA', 'Condición frente al IVA')}{' '}
                <span className="fiscal-required">*</span>{' '}
                <HelpIcon topicKey="fiscal.condicionIVA" />
              </label>
              <select
                id="fiscal-condicion-iva"
                value={config.condicionIVA}
                onChange={(e) => {
                  setConfig({ ...config, condicionIVA: e.target.value });
                  if (fieldErrors.condicionIVA)
                    setFieldErrors((prev) => ({ ...prev, condicionIVA: undefined }));
                }}
                required
                aria-required="true"
                aria-invalid={!!fieldErrors.condicionIVA}
                aria-describedby={fieldErrors.condicionIVA ? 'condicion-iva-error' : undefined}
              >
                <option value="">{t('fiscal.selectCondicion', 'Seleccionar...')}</option>
                {CONDICIONES_IVA_VALUES.map((val) => (
                  <option key={val} value={val}>
                    {t(`fiscal.ivaCondicion_${val}`)}
                  </option>
                ))}
              </select>
              {fieldErrors.condicionIVA && (
                <span id="condicion-iva-error" className="fiscal-field-error" role="alert">
                  {fieldErrors.condicionIVA}
                </span>
              )}
            </div>

            <div className="fiscal-form-row">
              <div className={`fiscal-form-group ${fieldErrors.puntoDeVenta ? 'has-error' : ''}`}>
                <label htmlFor="fiscal-punto-venta">
                  {t('fiscal.puntoDeVenta', 'Punto de Venta')}{' '}
                  <span className="fiscal-required">*</span>{' '}
                  <HelpIcon topicKey="fiscal.puntoVenta" />
                </label>
                <input
                  id="fiscal-punto-venta"
                  type="number"
                  value={config.puntoDeVenta}
                  onChange={(e) => {
                    setConfig({ ...config, puntoDeVenta: e.target.value });
                    if (fieldErrors.puntoDeVenta)
                      setFieldErrors((prev) => ({ ...prev, puntoDeVenta: undefined }));
                  }}
                  min={1}
                  max={99999}
                  placeholder="1"
                  required
                  aria-required="true"
                  aria-invalid={!!fieldErrors.puntoDeVenta}
                  aria-describedby={fieldErrors.puntoDeVenta ? 'punto-venta-error' : undefined}
                />
                {fieldErrors.puntoDeVenta && (
                  <span id="punto-venta-error" className="fiscal-field-error" role="alert">
                    {fieldErrors.puntoDeVenta}
                  </span>
                )}
              </div>
              <div className="fiscal-form-group">
                <label htmlFor="fiscal-iibb">{t('fiscal.iibb', 'IIBB')}</label>
                <input
                  id="fiscal-iibb"
                  type="text"
                  value={config.iibbNumber}
                  onChange={(e) => setConfig({ ...config, iibbNumber: e.target.value })}
                  placeholder={t('common.optional', 'Opcional')}
                />
              </div>
            </div>

            <div className="fiscal-form-group">
              <label htmlFor="fiscal-domicilio">
                {t('fiscal.domicilio', 'Domicilio Comercial')}
              </label>
              <input
                id="fiscal-domicilio"
                type="text"
                value={config.domicilioComercial}
                onChange={(e) => setConfig({ ...config, domicilioComercial: e.target.value })}
                placeholder="Dirección completa"
              />
            </div>

            <div className="fiscal-form-group">
              <label htmlFor="fiscal-environment">
                {t('fiscal.environment', 'Entorno AFIP')} <HelpIcon topicKey="fiscal.environment" />
              </label>
              <select
                id="fiscal-environment"
                value={config.afipEnvironment}
                onChange={(e) => setConfig({ ...config, afipEnvironment: e.target.value })}
              >
                <option value="homologacion">
                  {t('fiscal.testing', 'Homologación (Testing)')}
                </option>
                <option value="produccion">{t('fiscal.production', 'Producción')}</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.saving', 'Guardando...') : t('common.save', 'Guardar')}
            </button>
          </form>
        </div>

        {/* Certificado AFIP */}
        <div className="fiscal-card">
          <h2 className="fiscal-card-title">
            <span className="material-icons">security</span>
            {t('fiscal.certificate', 'Certificado AFIP')} <HelpIcon topicKey="fiscal.certificate" />
          </h2>

          {/* Mode toggle */}
          <div className="fiscal-cert-mode-toggle">
            <button
              type="button"
              className={`fiscal-cert-mode-btn ${certMode === 'guided' ? 'active' : ''}`}
              onClick={() => setCertMode('guided')}
            >
              <span className="material-icons">auto_fix_high</span>
              {t('fiscal.guidedMode', 'Asistido')}
            </button>
            <button
              type="button"
              className={`fiscal-cert-mode-btn ${certMode === 'manual' ? 'active' : ''}`}
              onClick={() => setCertMode('manual')}
            >
              <span className="material-icons">upload_file</span>
              {t('fiscal.manualMode', 'Manual')}
            </button>
          </div>

          {certMode === 'guided' ? (
            <>
              <p className="fiscal-card-description">
                {t(
                  'fiscal.guidedDescription',
                  'Generamos el archivo CSR por usted. Solo descárguelo, súbalo a AFIP y luego suba aquí el certificado firmado.'
                )}
              </p>

              {/* Step 1: Generate CSR */}
              <div className={`fiscal-step ${csrData?.csr ? 'completed' : 'active'}`}>
                <div className="fiscal-step-header">
                  <span className="fiscal-step-number">{csrData?.csr ? '✓' : '1'}</span>
                  <span className="fiscal-step-title">
                    {t('fiscal.step1Title', 'Generar solicitud de certificado (CSR)')}
                  </span>
                </div>
                <div className="fiscal-step-body">
                  {!csrData?.csr ? (
                    <>
                      <p className="fiscal-step-description">
                        {t(
                          'fiscal.step1Desc',
                          'Se generará un archivo CSR (PKCS#10) con los datos de su empresa. La clave privada se almacena de forma segura.'
                        )}
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleGenerateCSR}
                        disabled={generatingCSR || !config.cuit || !config.razonSocial}
                      >
                        <span className="material-icons">vpn_key</span>
                        {generatingCSR
                          ? t('fiscal.generating', 'Generando...')
                          : t('fiscal.generateCSR', 'Generar CSR')}
                      </button>
                      {(!config.cuit || !config.razonSocial) && (
                        <p className="fiscal-step-hint">
                          {t('fiscal.csrHint', 'Complete CUIT y Razón Social primero')}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="fiscal-step-description fiscal-step-description--success">
                        {t('fiscal.csrReady', 'CSR generado')}
                        {csrData.generatedAt &&
                          ` (${new Date(csrData.generatedAt).toLocaleDateString()})`}
                      </p>
                      {csrData.csr && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleDownloadCSR}
                        >
                          <span className="material-icons">download</span>
                          {t('fiscal.downloadCSR', 'Descargar CSR')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Step 2: Upload to AFIP */}
              <div className={`fiscal-step ${csrData?.csr ? 'active' : 'pending'}`}>
                <div className="fiscal-step-header">
                  <span className="fiscal-step-number">2</span>
                  <span className="fiscal-step-title">
                    {t('fiscal.step2Title', 'Subir CSR a AFIP')}
                  </span>
                </div>
                <div className="fiscal-step-body">
                  <ol className="fiscal-step-instructions">
                    <li>{t('fiscal.instruction1', 'Ingrese a AFIP con su CUIT y clave fiscal')}</li>
                    <li>
                      {t(
                        'fiscal.instruction2',
                        'Busque "Administración de Certificados Digitales"'
                      )}
                    </li>
                    <li>
                      {t(
                        'fiscal.instruction3',
                        'Haga clic en "Agregar alias" o "Nuevo certificado"'
                      )}
                    </li>
                    <li>
                      {t(
                        'fiscal.instruction4',
                        'Suba el archivo .csr descargado en el paso anterior'
                      )}
                    </li>
                    <li>
                      {t('fiscal.instruction5', 'AFIP le devolverá un certificado firmado (.crt)')}
                    </li>
                  </ol>
                </div>
              </div>

              {/* Step 3: Upload signed cert */}
              <div
                className={`fiscal-step ${hasCertificate ? 'completed' : csrData?.csr ? 'active' : 'pending'}`}
              >
                <div className="fiscal-step-header">
                  <span className="fiscal-step-number">{hasCertificate ? '✓' : '3'}</span>
                  <span className="fiscal-step-title">
                    {t('fiscal.step3Title', 'Subir certificado firmado')}
                  </span>
                </div>
                <div className="fiscal-step-body">
                  {hasCertificate ? (
                    <p className="fiscal-step-description fiscal-step-description--success">
                      {t('fiscal.certConfigured', 'Certificado configurado correctamente')}
                    </p>
                  ) : (
                    <>
                      <div className="fiscal-form-group">
                        <label htmlFor="fiscal-cert-guided">
                          {t('fiscal.certFile', 'Certificado (.crt)')}
                        </label>
                        <input
                          id="fiscal-cert-guided"
                          type="file"
                          accept=".crt,.pem,.cert"
                          onChange={(e) => setCertFile(e.target.files[0])}
                          disabled={!csrData}
                        />
                        {certFile && <span className="fiscal-file-name">{certFile.name}</span>}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUploadCertificate}
                        disabled={uploadingCert || !certFile || !csrData}
                      >
                        {uploadingCert
                          ? t('fiscal.uploading', 'Subiendo...')
                          : t('fiscal.uploadSignedCert', 'Subir Certificado Firmado')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="fiscal-card-description">
                {t(
                  'fiscal.manualDescription',
                  'Suba el certificado digital (.crt) y la clave privada (.key) si ya los tiene generados.'
                )}
              </p>

              <div className="fiscal-form-group">
                <label htmlFor="fiscal-cert-manual">
                  {t('fiscal.certFile', 'Certificado (.crt)')}
                </label>
                <input
                  id="fiscal-cert-manual"
                  type="file"
                  accept=".crt,.pem,.cert"
                  onChange={(e) => setCertFile(e.target.files[0])}
                />
                {certFile && <span className="fiscal-file-name">{certFile.name}</span>}
              </div>

              <div className="fiscal-form-group">
                <label htmlFor="fiscal-key-manual">
                  {t('fiscal.keyFile', 'Clave Privada (.key)')}
                </label>
                <input
                  id="fiscal-key-manual"
                  type="file"
                  accept=".key,.pem"
                  onChange={(e) => setKeyFile(e.target.files[0])}
                />
                {keyFile && <span className="fiscal-file-name">{keyFile.name}</span>}
              </div>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUploadCertificate}
                disabled={uploadingCert || !certFile || !keyFile}
              >
                {uploadingCert
                  ? t('fiscal.uploading', 'Subiendo...')
                  : t('fiscal.uploadCert', 'Subir Certificado')}
              </button>
            </>
          )}

          <div className="fiscal-divider" />

          <h3 className="fiscal-card-subtitle">{t('fiscal.testConnection', 'Probar Conexión')}</h3>
          <div className="fiscal-cert-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleTestConnection}
              disabled={testingConnection || !hasCertificate}
            >
              <span className="material-icons">wifi_tethering</span>
              {testingConnection
                ? t('fiscal.testing', 'Verificando...')
                : t('fiscal.testAfip', 'Verificar Conexión AFIP')}
            </button>
            {hasCertificate && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleDeleteCertificate}
              >
                <span className="material-icons">delete</span>
                {t('fiscal.deleteCert', 'Eliminar Certificado')}
              </button>
            )}
          </div>

          {afipStatus?.serverStatus && (
            <div className="fiscal-server-status">
              <div
                className={`status-row ${afipStatus.serverStatus.AppServer === 'OK' ? 'ok' : 'error'}`}
              >
                <span>AppServer:</span> <span>{afipStatus.serverStatus.AppServer}</span>
              </div>
              <div
                className={`status-row ${afipStatus.serverStatus.DbServer === 'OK' ? 'ok' : 'error'}`}
              >
                <span>DbServer:</span> <span>{afipStatus.serverStatus.DbServer}</span>
              </div>
              <div
                className={`status-row ${afipStatus.serverStatus.AuthServer === 'OK' ? 'ok' : 'error'}`}
              >
                <span>AuthServer:</span> <span>{afipStatus.serverStatus.AuthServer}</span>
              </div>
            </div>
          )}

          {/* Help: not authorized error */}
          {afipStatus?.status === 'error' && afipStatus?.error?.includes('notAuthorized') && (
            <div className="fiscal-help-box">
              <h4>
                <span className="material-icons">help_outline</span>
                {t('fiscal.authHelpTitle', 'El certificado no está autorizado para facturar')}
              </h4>
              <p>
                {t(
                  'fiscal.authHelpDesc',
                  'El certificado se conectó correctamente, pero necesita ser autorizado en AFIP para el servicio de Factura Electrónica.'
                )}
              </p>
              <ol>
                <li>{t('fiscal.authStep1', 'Ingrese a AFIP con su CUIT y clave fiscal')}</li>
                <li>
                  {t('fiscal.authStep2', 'Busque "Administración de Relaciones de Clave Fiscal"')}
                </li>
                <li>
                  {t('fiscal.authStep3', 'Haga clic en "Nueva relación" o "Adherir servicio"')}
                </li>
                <li>
                  {t('fiscal.authStep4', 'Busque ARCA > WebServices > Facturación Electrónica')}
                </li>
                <li>
                  {t(
                    'fiscal.authStep5',
                    'Seleccione el computador/alias del certificado que generó'
                  )}
                </li>
                <li>
                  {t(
                    'fiscal.authStep6',
                    'Confirme la relación y vuelva a verificar la conexión aquí'
                  )}
                </li>
              </ol>
            </div>
          )}

          {/* Diagnostics panel (collapsible) */}
          {afipStatus?.diagnostics && (
            <details className="fiscal-diagnostics">
              <summary>{t('fiscal.diagnostics', 'Diagnósticos técnicos')}</summary>
              <pre className="fiscal-diagnostics-content">
                {JSON.stringify(afipStatus.diagnostics, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default FiscalConfigPage;
